// ============================================================================
// WEBRTC PEER CONNECTION MANAGER
// ============================================================================

import { EventEmitter } from './EventEmitter';

export interface PeerConfig {
  iceServers?: RTCIceServer[];
}

export interface PeerEvents {
  connected: { peerId: string };
  disconnected: { peerId: string };
  message: { peerId: string; data: ArrayBuffer | string };
  error: { peerId: string; error: Error };
}

const DEFAULT_ICE_SERVERS: RTCIceServer[] = [
  { urls: 'stun:stun.l.google.com:19302' },
  { urls: 'stun:stun1.l.google.com:19302' },
];

export class PeerConnection extends EventEmitter<PeerEvents> {
  private peerId: string;
  private connection: RTCPeerConnection;
  private dataChannel: RTCDataChannel | null = null;
  private isConnected = false;

  constructor(peerId: string, config?: PeerConfig) {
    super();
    this.peerId = peerId;

    this.connection = new RTCPeerConnection({
      iceServers: config?.iceServers ?? DEFAULT_ICE_SERVERS,
    });

    this.setupConnectionListeners();
  }

  private setupConnectionListeners(): void {
    this.connection.oniceconnectionstatechange = () => {
      const state = this.connection.iceConnectionState;
      console.log(`[Peer ${this.peerId}] ICE state: ${state}`);

      if (state === 'connected') {
        // Only emit connected once
        if (!this.isConnected) {
          this.isConnected = true;
          this.emit('connected', { peerId: this.peerId });
        }
      } else if (state === 'disconnected' || state === 'failed' || state === 'closed') {
        this.isConnected = false;
        this.emit('disconnected', { peerId: this.peerId });
      }
    };

    this.connection.ondatachannel = (event) => {
      this.setupDataChannel(event.channel);
    };
  }

  private setupDataChannel(channel: RTCDataChannel): void {
    this.dataChannel = channel;
    this.dataChannel.binaryType = 'arraybuffer';

    this.dataChannel.onopen = () => {
      console.log(`[Peer ${this.peerId}] Data channel opened`);
    };

    this.dataChannel.onclose = () => {
      console.log(`[Peer ${this.peerId}] Data channel closed`);
    };

    this.dataChannel.onmessage = (event) => {
      this.emit('message', { peerId: this.peerId, data: event.data });
    };

    this.dataChannel.onerror = (event) => {
      this.emit('error', { peerId: this.peerId, error: new Error('Data channel error') });
      console.error(`[Peer ${this.peerId}] Data channel error:`, event);
    };
  }

  // Create offer (initiator)
  async createOffer(): Promise<RTCSessionDescriptionInit> {
    // Create data channel before offer
    const channel = this.connection.createDataChannel('game', {
      ordered: false,          // Unordered for speed
      maxRetransmits: 0,       // No retransmits for real-time
    });
    this.setupDataChannel(channel);

    const offer = await this.connection.createOffer();
    await this.connection.setLocalDescription(offer);
    return offer;
  }

  // Handle offer (receiver)
  async handleOffer(offer: RTCSessionDescriptionInit): Promise<RTCSessionDescriptionInit> {
    await this.connection.setRemoteDescription(offer);
    const answer = await this.connection.createAnswer();
    await this.connection.setLocalDescription(answer);
    return answer;
  }

  // Handle answer (initiator)
  async handleAnswer(answer: RTCSessionDescriptionInit): Promise<void> {
    await this.connection.setRemoteDescription(answer);
  }

  // Add ICE candidate
  async addIceCandidate(candidate: RTCIceCandidateInit): Promise<void> {
    try {
      await this.connection.addIceCandidate(candidate);
    } catch (e) {
      console.warn(`[Peer ${this.peerId}] Failed to add ICE candidate:`, e);
    }
  }

  // Get ICE candidates
  onIceCandidate(callback: (candidate: RTCIceCandidate) => void): void {
    this.connection.onicecandidate = (event) => {
      if (event.candidate) {
        callback(event.candidate);
      }
    };
  }

  // Send data
  send(data: ArrayBuffer | string): boolean {
    if (!this.dataChannel || this.dataChannel.readyState !== 'open') {
      return false;
    }

    try {
      if (typeof data === 'string') {
        this.dataChannel.send(data);
      } else {
        this.dataChannel.send(new Uint8Array(data));
      }
      return true;
    } catch (e) {
      console.error(`[Peer ${this.peerId}] Send failed:`, e);
      return false;
    }
  }

  // Send with buffering check
  sendIfReady(data: ArrayBuffer | string): boolean {
    if (!this.dataChannel || this.dataChannel.readyState !== 'open') {
      return false;
    }

    // Don't send if buffer is getting full
    if (this.dataChannel.bufferedAmount > 65536) {
      console.warn(`[Peer ${this.peerId}] Buffer full, skipping send`);
      return false;
    }

    return this.send(data);
  }

  close(): void {
    this.dataChannel?.close();
    this.connection.close();
    this.isConnected = false;
  }

  getPeerId(): string {
    return this.peerId;
  }

  getIsConnected(): boolean {
    return this.isConnected && this.dataChannel?.readyState === 'open';
  }

  getStats(): Promise<RTCStatsReport> {
    return this.connection.getStats();
  }

  // Get the actual RTCPeerConnection (for voice chat)
  getRTCConnection(): RTCPeerConnection {
    return this.connection;
  }
}
